<template>
  <div class="page-stack">
    <section class="section-shell section-shell--hero">
      <div class="section-head">
        <div>
          <div class="section-kicker">Products</div>
          <h3 class="section-title">产品列表</h3>
          <p class="section-subtitle">产品编辑页只负责媒体导入和提示词生成；生成后的信息、日志和 Etsy 操作都在列表里查看。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <input v-model.trim="keyword" class="field-input min-w-[240px]" placeholder="搜索标题或状态">
          <button class="btn btn-primary" @click="openCreateModal">创建产品</button>
          <button class="btn btn-secondary" @click="openLogsModal(null)">系统日志</button>
          <button class="btn btn-warm" :disabled="!selectedIds.length" @click="exportCsv">
            导出 CSV（{{ selectedIds.length }}）
          </button>
          <button class="btn btn-secondary" @click="loadAll">刷新</button>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Archive</div>
          <h4 class="section-title">历史记录</h4>
          <p class="section-subtitle">支持查看信息、查看操作日志、重新生成 JSON，以及直接发送到 Etsy draft。</p>
        </div>
        <div class="text-sm text-slate-400">共 {{ pagination.total }} 条</div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th><input type="checkbox" @change="toggleAllSelection($event)"></th>
              <th>产品</th>
              <th>媒体</th>
              <th>JSON / Etsy</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in products" :key="item.id">
              <td><input v-model="selectedIds" :value="item.id" type="checkbox"></td>
              <td class="min-w-[280px]">
                <div class="font-semibold text-slate-900">{{ item.title || `草稿 #${item.id}` }}</div>
                <div class="mt-2 text-sm text-slate-500">{{ item.category || '等待应用 AI 结果' }}</div>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span class="badge" :class="badgeClass(item.generationStatus)">
                    {{ item.generationStatus || 'DRAFT' }}
                  </span>
                  <span v-if="item.etsyListingId" class="badge badge-success">Listing #{{ item.etsyListingId }}</span>
                </div>
              </td>
              <td class="min-w-[220px]">
                <div class="flex flex-wrap gap-2">
                  <img
                    v-for="img in previewImages(item)"
                    :key="img.id"
                    :src="img.publicUrl"
                    alt=""
                    class="h-14 w-14 rounded-xl object-cover"
                  >
                </div>
                <div class="mt-3 text-sm text-slate-500">
                  {{ countImages(item.mediaAssets) }} 张图片 / {{ countVideos(item.mediaAssets) }} 个视频
                </div>
              </td>
              <td class="min-w-[220px] text-sm text-slate-500">
                <div>AI JSON：{{ item.aiResultJson ? '已生成' : '未生成' }}</div>
                <div class="mt-2">Etsy：{{ item.etsyListingUrl ? '已发送' : '未发送' }}</div>
                <a
                  v-if="item.etsyListingUrl"
                  :href="item.etsyListingUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mt-2 inline-flex text-sm font-semibold text-slate-900 underline"
                >
                  查看 Etsy 草稿
                </a>
              </td>
              <td class="text-sm text-slate-500">{{ item.createdAt || '-' }}</td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="btn btn-secondary" @click="openInfoModal(item.id)">信息</button>
                  <button class="btn btn-secondary" @click="openEditModal(item.id)">编辑</button>
                  <button class="btn btn-secondary" @click="openLogsModal(item)">日志</button>
                  <button class="btn btn-warm" @click="publishToEtsy(item.id)">发到 Etsy</button>
                  <button class="btn btn-secondary" @click="removeProduct(item.id)">删除</button>
                </div>
              </td>
            </tr>
            <tr v-if="!products.length">
              <td colspan="6" class="table-empty">当前没有产品记录</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        :page-num="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :total-pages="pagination.totalPages"
        @change-page="handlePageChange"
        @change-size="handlePageSizeChange"
      />
    </section>

    <div v-if="editorVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel modal-panel--xl" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">{{ form.id ? '编辑产品生成' : '创建产品生成' }}</h4>
            <p class="mt-2 text-sm text-slate-500">这里只保留媒体组、视频地址和提示词；AI JSON 会先展示，确认后再应用。</p>
          </div>
          <button class="btn btn-secondary" @click="closeEditorModal">关闭</button>
        </div>

        <div class="modal-panel__body mt-6">
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="field-label">变量字典模板</label>
              <select v-model="form.variationTemplateId" class="field-select" @change="handleEditorVariationTemplateChange">
                <option :value="null">不使用</option>
                <option v-for="item in variationTemplates" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
              <div class="settings-hint">用于维护 Section、默认库存、Variation 名称和不同组合价格。</div>
            </div>
            <div>
              <label class="field-label">变量组合</label>
              <select
                v-model="form.variationTemplateItemId"
                class="field-select"
                :disabled="!selectedVariationTemplate?.items?.length"
                @change="handleEditorVariationItemChange"
              >
                <option :value="null">{{ selectedVariationTemplate?.items?.length ? '选择组合' : '请先选择变量模板' }}</option>
                <option
                  v-for="item in selectedVariationItems"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ formatVariationItemLabel(selectedVariationTemplate, item) }}
                </option>
              </select>
            </div>
            <div>
              <label class="field-label">Etsy Section</label>
              <select v-model="form.sectionId" class="field-select" @change="handleEditorSectionChange">
                <option :value="null">不选择</option>
                <option v-for="item in etsySections" :key="item.sectionId" :value="item.sectionId">{{ item.title }}</option>
              </select>
              <div class="settings-hint">
                来自 Etsy 店铺官方 Section。发布草稿后系统会自动调用官方 listing 更新接口绑定 section。
              </div>
            </div>
            <div>
              <label class="field-label">运费模板</label>
              <select v-model="form.shippingProfileId" class="field-select">
                <option :value="null">自动解析</option>
                <option v-for="item in etsyShippingProfiles" :key="item.shippingProfileId" :value="item.shippingProfileId">
                  {{ item.title || `Profile #${item.shippingProfileId}` }}
                </option>
              </select>
              <div class="settings-hint">这里直接绑定 Etsy 店铺里的官方 shipping profile，不再手输 ID。</div>
            </div>
            <div>
              <label class="field-label">提示词模板</label>
              <select v-model="descriptionTemplateId" class="field-select">
                <option value="">不选择</option>
                <option v-for="item in descriptionPrompts" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
              <div v-if="selectedDescriptionTemplate?.variables?.length" class="settings-hint">
                当前模板包含 {{ selectedDescriptionTemplate.variables.length }} 个变量，请在下方填写后再生成。
              </div>
            </div>
            <div>
              <label class="field-label">Video URL</label>
              <input v-model.trim="form.video1" class="field-input" placeholder="选中视频后会自动回填">
            </div>
          </div>

          <div v-if="selectedVariationTemplate" class="mt-4 rounded-[1.4rem] border border-slate-200 bg-white/70 p-4">
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <div class="field-label">Section</div>
                <div class="text-sm text-slate-700">{{ form.sectionName || selectedVariationTemplate.sectionName || '-' }}</div>
              </div>
              <div>
                <div class="field-label">基础价格</div>
                <div class="text-sm text-slate-700">{{ selectedVariationTemplate.basePrice ?? '-' }}</div>
              </div>
              <div>
                <div class="field-label">默认库存</div>
                <div class="text-sm text-slate-700">{{ selectedVariationTemplate.defaultQuantity ?? 25 }}</div>
              </div>
              <div>
                <div class="field-label">组合价格</div>
                <div class="text-sm text-slate-700">{{ selectedVariationItem?.varPrice ?? '-' }}</div>
              </div>
            </div>
            <div class="mt-4">
              <div class="field-label">运费模板</div>
              <div class="text-sm text-slate-700">{{ selectedShippingProfileLabel(form.shippingProfileId) }}</div>
            </div>
            <div class="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <div class="field-label">{{ form.variation1 || selectedVariationTemplate.variation1Name || 'Variation 1' }}</div>
                <div class="text-sm text-slate-700">{{ form.v1Option || '-' }}</div>
              </div>
              <div>
                <div class="field-label">{{ form.variation2 || selectedVariationTemplate.variation2Name || 'Variation 2' }}</div>
                <div class="text-sm text-slate-700">{{ form.v2Option || '-' }}</div>
              </div>
            </div>
          </div>

          <div v-if="selectedTemplateVariables.length" class="mt-4 rounded-[1.4rem] border border-slate-200 bg-white/70 p-4">
            <div class="field-label">模板变量值</div>
            <div class="grid gap-4 md:grid-cols-2">
              <div v-for="item in selectedTemplateVariables" :key="item.name">
                <label class="field-label">{{ item.name }}</label>
                <input
                  v-model.trim="templateVariableValues[item.name]"
                  class="field-input"
                  :placeholder="`请输入 ${item.name}`"
                >
              </div>
            </div>
          </div>

        <div class="mt-4">
          <label class="field-label">AI 提示词</label>
          <textarea
            v-model.trim="form.descriptionPrompt"
            class="field-textarea"
            placeholder="可填写附加说明；如果已选择模板，这里会作为附加提示词追加到模板后"
          />
          <div class="mt-3 flex flex-wrap gap-3">
            <button class="btn btn-secondary" type="button" @click="openPromptSaveModal">保存到提示词库</button>
          </div>
        </div>

          <div class="mt-6 rounded-[1.6rem] border border-dashed border-slate-300 bg-white/70 p-5">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h5 class="text-lg font-bold text-slate-900">媒体组</h5>
                <p class="mt-2 text-sm text-slate-500">
                  一次可导入 1-20 张图片和 1 个视频，地址会自动保存并用于回显、导出和 Etsy 上传。
                </p>
              </div>
              <input type="file" multiple accept="image/*,video/*" @change="handleFiles">
            </div>
            <div class="mt-4 flex flex-wrap gap-3 text-sm text-slate-500">
              <span>已选 {{ countImages(selectedMedia) }} 张图片</span>
              <span>已选 {{ countVideos(selectedMedia) }} 个视频</span>
            </div>
            <div class="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <label
                v-for="item in mediaAssets"
                :key="item.id"
                class="overflow-hidden rounded-[1.4rem] border border-slate-200 bg-white shadow-sm"
              >
                <div class="relative aspect-[4/3] bg-slate-100">
                  <img
                    v-if="item.imageType !== 'VIDEO'"
                    :src="item.publicUrl"
                    alt=""
                    class="h-full w-full object-cover"
                  >
                  <video
                    v-else
                    :src="item.publicUrl"
                    class="h-full w-full object-cover"
                    controls
                    preload="metadata"
                  />
                  <input
                    v-model="selectedMediaIds"
                    :value="item.id"
                    type="checkbox"
                    class="absolute left-3 top-3 h-5 w-5 rounded"
                  >
                  <span class="absolute bottom-3 left-3 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-semibold text-white">
                    {{ item.imageType || 'IMAGE' }}
                  </span>
                </div>
                <div class="space-y-3 p-4">
                  <div class="text-sm font-semibold text-slate-900">{{ item.originalName || '媒体文件' }}</div>
                  <input :value="item.publicUrl" class="field-input text-xs" readonly @focus="$event.target.select()">
                  <button class="btn btn-secondary w-full" type="button" @click.prevent="copyText(item.publicUrl)">
                    复制地址
                  </button>
                </div>
              </label>
            </div>
          </div>

          <div v-if="previewResult.aiResultJson" class="mt-6 rounded-[1.6rem] border border-slate-200 bg-white/70 p-5">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div class="text-lg font-bold text-slate-900">AI JSON 预览</div>
                <div class="mt-2 text-sm text-slate-500">日志已经保存。确认无误后，再把这份结果应用到产品信息。</div>
              </div>
              <div class="flex flex-wrap gap-3">
                <button class="btn btn-secondary" type="button" @click="copyText(previewResult.aiResultJson)">复制 JSON</button>
                <button class="btn btn-primary" type="button" @click="applyAiResult">应用到产品</button>
              </div>
            </div>
            <pre class="code-panel mt-4">{{ previewResult.aiResultJson }}</pre>
          </div>

          <div v-else-if="form.aiResultJson" class="mt-6 rounded-[1.6rem] border border-slate-200 bg-white/70 p-5">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div class="text-lg font-bold text-slate-900">当前已应用 JSON</div>
                <div class="mt-2 text-sm text-slate-500">这是当前产品已经保存到系统的信息快照。</div>
              </div>
              <button class="btn btn-secondary" type="button" @click="copyText(form.aiResultJson)">复制 JSON</button>
            </div>
            <pre class="code-panel mt-4">{{ form.aiResultJson }}</pre>
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-primary" @click="saveProduct">{{ form.id ? '保存媒体与提示词' : '创建产品' }}</button>
          <button class="btn btn-secondary" :disabled="!form.id" @click="generateDescription">生成 JSON</button>
        </div>
      </section>
    </div>

    <div v-if="infoVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel modal-panel--lg" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">产品信息</h4>
            <p class="mt-2 text-sm text-slate-500">这里可以直接编辑 AI 已应用的信息，包括价格、库存、标题、描述和 Etsy 相关字段。</p>
          </div>
          <button class="btn btn-secondary" @click="closeInfoModal">关闭</button>
        </div>

        <div v-if="infoForm.id" class="modal-panel__body mt-6">
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="field-label">变量字典模板</label>
              <select v-model="infoForm.variationTemplateId" class="field-select" @change="handleInfoVariationTemplateChange">
                <option :value="null">不使用</option>
                <option v-for="item in variationTemplates" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
            </div>
            <div>
              <label class="field-label">变量组合</label>
              <select
                v-model="infoForm.variationTemplateItemId"
                class="field-select"
                :disabled="!selectedInfoVariationTemplate?.items?.length"
                @change="handleInfoVariationItemChange"
              >
                <option :value="null">{{ selectedInfoVariationTemplate?.items?.length ? '选择组合' : '请先选择变量模板' }}</option>
                <option
                  v-for="item in selectedInfoVariationItems"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ formatVariationItemLabel(selectedInfoVariationTemplate, item) }}
                </option>
              </select>
            </div>
            <div>
              <label class="field-label">变体同步方式</label>
              <select v-model="infoForm.etsyVariationSelection.mode" class="field-select" @change="handleInfoVariationModeChange">
                <option value="NONE">不启用</option>
                <option value="TEMPLATE">使用变量模板</option>
                <option value="OFFICIAL">使用 Etsy 官方属性</option>
              </select>
            </div>
            <div>
              <label class="field-label">Etsy Section</label>
              <select v-model="infoForm.sectionId" class="field-select" @change="handleInfoSectionChange">
                <option :value="null">不选择</option>
                <option v-for="item in etsySections" :key="item.sectionId" :value="item.sectionId">{{ item.title }}</option>
              </select>
            </div>
            <div>
              <label class="field-label">运费模板</label>
              <select v-model="infoForm.shippingProfileId" class="field-select">
                <option :value="null">自动解析</option>
                <option v-for="item in etsyShippingProfiles" :key="item.shippingProfileId" :value="item.shippingProfileId">
                  {{ item.title || `Profile #${item.shippingProfileId}` }}
                </option>
              </select>
            </div>
            <div>
              <label class="field-label">Title</label>
              <input v-model.trim="infoForm.title" class="field-input">
            </div>
            <div>
              <label class="field-label">Category</label>
              <input v-model.trim="infoForm.category" class="field-input">
            </div>
            <div>
              <label class="field-label">Price</label>
              <input v-model.number="infoForm.price" class="field-input" type="number" step="0.01">
            </div>
            <div>
              <label class="field-label">Quantity</label>
              <input v-model.number="infoForm.quantity" class="field-input" type="number" step="1">
            </div>
            <div>
              <label class="field-label">SKU</label>
              <input v-model.trim="infoForm.sku" class="field-input">
            </div>
            <div>
              <label class="field-label">Materials</label>
              <input v-model.trim="infoForm.materials" class="field-input">
            </div>
            <div>
              <label class="field-label">Variation 1</label>
              <input v-model.trim="infoForm.variation1" class="field-input" maxlength="45">
            </div>
            <div>
              <label class="field-label">V1 Option</label>
              <input v-model.trim="infoForm.v1Option" class="field-input" maxlength="20">
            </div>
            <div>
              <label class="field-label">Variation 2</label>
              <input v-model.trim="infoForm.variation2" class="field-input" maxlength="45">
            </div>
            <div>
              <label class="field-label">V2 Option</label>
              <input v-model.trim="infoForm.v2Option" class="field-input" maxlength="20">
            </div>
            <div>
              <label class="field-label">Var Price</label>
              <input v-model.number="infoForm.varPrice" class="field-input" type="number" step="0.01">
            </div>
            <div>
              <label class="field-label">Production Partners</label>
              <input v-model.trim="infoForm.productionPartners" class="field-input">
            </div>
            <div>
              <label class="field-label">Product Type</label>
              <input v-model.trim="infoForm.productType" class="field-input">
            </div>
            <div>
              <label class="field-label">Who Made</label>
              <select v-model="infoForm.whoMade" class="field-select">
                <option v-for="item in etsyWhoMadeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
              </select>
            </div>
            <div>
              <label class="field-label">When Made</label>
              <select v-model="infoForm.whenWasItMade" class="field-select">
                <option v-for="item in etsyWhenMadeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
              </select>
            </div>
            <div>
              <label class="field-label">Video URL</label>
              <input v-model.trim="infoForm.video1" class="field-input">
            </div>
          </div>

          <div class="mt-4 rounded-[1.4rem] border border-slate-200 bg-white/80 p-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="field-label">变体同步配置</div>
                <div class="text-sm text-slate-500">当前方式：{{ variationModeLabel(infoForm.etsyVariationSelection.mode) }}</div>
              </div>
              <button
                v-if="infoForm.etsyVariationSelection.mode === 'OFFICIAL'"
                class="btn btn-secondary"
                type="button"
                @click="loadInfoVariationMetadata"
              >
                加载 Etsy 官方属性
              </button>
            </div>

            <div v-if="infoForm.etsyVariationSelection.mode === 'TEMPLATE'" class="mt-4 text-sm text-slate-600">
              发布时会按当前绑定的变量模板全部组合自动同步到 Etsy inventory。要让 `has_variations` 成功，模板里至少需要 2 个组合项。
            </div>

            <div v-if="infoForm.etsyVariationSelection.mode === 'OFFICIAL'" class="mt-4 space-y-4">
              <div class="text-sm text-slate-600">
                Taxonomy：{{ etsyVariationMetadata?.taxonomyName || '-' }} / ID：{{ etsyVariationMetadata?.taxonomyId || infoForm.taxonomyId || '-' }}
              </div>
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <label class="field-label">官方属性 1</label>
                  <select
                    class="field-select"
                    :value="infoForm.etsyVariationSelection.property1?.propertyId ?? null"
                    @change="handleOfficialPropertyChange('property1', $event.target.value)"
                  >
                    <option :value="null">请选择</option>
                    <option v-for="item in officialPropertyOptions()" :key="item.propertyId" :value="item.propertyId">{{ item.name }}</option>
                  </select>
                </div>
                <div>
                  <label class="field-label">官方属性 2</label>
                  <select
                    class="field-select"
                    :value="infoForm.etsyVariationSelection.property2?.propertyId ?? null"
                    @change="handleOfficialPropertyChange('property2', $event.target.value)"
                  >
                    <option :value="null">不使用</option>
                    <option v-for="item in officialPropertyOptions()" :key="`p2-${item.propertyId}`" :value="item.propertyId">{{ item.name }}</option>
                  </select>
                </div>
              </div>

              <div v-if="infoForm.etsyVariationSelection.property1" class="rounded-[1rem] border border-slate-200 bg-white/70 p-4">
                <div class="field-label">{{ infoForm.etsyVariationSelection.property1.name }} 值</div>
                <div class="mt-3 flex flex-wrap gap-3">
                  <label
                    v-for="value in findOfficialProperty(infoForm.etsyVariationSelection.property1.propertyId)?.values || []"
                    :key="`p1-${value.valueId}-${value.value}`"
                    class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      :checked="isOfficialValueChecked(infoForm, 'property1', value)"
                      @change="toggleOfficialValue(infoForm, 'property1', value, $event.target.checked)"
                    >
                    <span>{{ value.value }}</span>
                  </label>
                </div>
              </div>

              <div v-if="infoForm.etsyVariationSelection.property2" class="rounded-[1rem] border border-slate-200 bg-white/70 p-4">
                <div class="field-label">{{ infoForm.etsyVariationSelection.property2.name }} 值</div>
                <div class="mt-3 flex flex-wrap gap-3">
                  <label
                    v-for="value in findOfficialProperty(infoForm.etsyVariationSelection.property2.propertyId)?.values || []"
                    :key="`p2-${value.valueId}-${value.value}`"
                    class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      :checked="isOfficialValueChecked(infoForm, 'property2', value)"
                      @change="toggleOfficialValue(infoForm, 'property2', value, $event.target.checked)"
                    >
                    <span>{{ value.value }}</span>
                  </label>
                </div>
              </div>

              <div class="text-sm text-slate-600">
                预计同步组合数：{{ officialVariationCount() }}。至少需要 2 个组合，Etsy 才会识别为 variations。
              </div>
            </div>
          </div>

          <div class="mt-4">
            <label class="field-label">Tags</label>
            <input v-model.trim="infoForm.tags" class="field-input">
          </div>

          <div class="mt-4">
            <label class="field-label">Description</label>
            <textarea v-model.trim="infoForm.descriptionText" class="field-textarea" />
          </div>

          <div class="mt-4 rounded-[1.4rem] border border-slate-200 bg-white/80 p-4">
            <div class="field-label">Etsy 状态</div>
            <div class="text-sm text-slate-700">
              Listing ID：{{ infoForm.etsyListingId || '-' }}
            </div>
            <div class="mt-2 text-sm text-slate-700">Taxonomy ID：{{ infoForm.taxonomyId || '自动匹配' }}</div>
            <div class="mt-1 text-sm text-slate-700">Shipping Profile：{{ selectedShippingProfileLabel(infoForm.shippingProfileId) }}</div>
            <div class="mt-1 text-sm text-slate-700">Readiness State ID：{{ infoForm.readinessStateId || '自动解析' }}</div>
            <a
              v-if="infoForm.etsyListingUrl"
              :href="infoForm.etsyListingUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-2 inline-flex text-sm font-semibold text-slate-900 underline"
            >
              打开 Etsy 草稿
            </a>
          </div>

          <div v-if="infoForm.aiResultJson" class="mt-4 rounded-[1.4rem] border border-slate-200 bg-white/80 p-4">
            <div class="flex items-center justify-between gap-3">
              <div class="field-label">AI JSON</div>
              <button class="btn btn-secondary" type="button" @click="copyText(infoForm.aiResultJson)">复制 JSON</button>
            </div>
            <pre class="code-panel mt-3">{{ infoForm.aiResultJson }}</pre>
          </div>

          <div class="modal-panel__footer">
            <button class="btn btn-primary" type="button" @click="saveInfoForm">保存信息</button>
            <button class="btn btn-secondary" type="button" @click="closeInfoModal">关闭</button>
          </div>
        </div>
      </section>
    </div>

    <div v-if="promptSaveVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">保存提示词</h4>
            <p class="mt-2 text-sm text-slate-500">为当前提示词手动设置名称，保存后下次可直接下拉复用。</p>
          </div>
          <button class="btn btn-secondary" @click="closePromptSaveModal">关闭</button>
        </div>
        <div class="modal-panel__body mt-6 space-y-4">
          <div>
            <label class="field-label">提示词名称</label>
            <input v-model.trim="promptSaveForm.name" class="field-input" placeholder="例如：Slim Paw Gloves SEO JSON">
          </div>
          <div>
            <label class="field-label">提示词内容</label>
            <textarea v-model.trim="promptSaveForm.content" class="field-textarea" />
          </div>
        </div>
        <div class="modal-panel__footer">
          <button class="btn btn-primary" type="button" @click="savePromptToLibrary">保存到提示词库</button>
          <button class="btn btn-secondary" type="button" @click="closePromptSaveModal">取消</button>
        </div>
      </section>
    </div>

    <div v-if="logsVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel modal-panel--lg" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">操作日志</h4>
            <p class="mt-2 text-sm text-slate-500">
              {{ logTargetTitle ? `当前产品：${logTargetTitle}` : '系统全部日志' }}，会保存生成预览、应用 JSON 和 Etsy 发送记录。
            </p>
          </div>
          <button class="btn btn-secondary" @click="closeLogsModal">关闭</button>
        </div>

        <div class="modal-panel__body mt-6">
          <table class="data-table">
            <thead>
              <tr>
                <th>时间</th>
                <th>产品</th>
                <th>任务</th>
                <th>结果</th>
                <th>说明</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in logs" :key="item.id">
                <td class="text-sm text-slate-500">{{ item.createdAt }}</td>
                <td class="font-semibold text-slate-900">{{ item.productTitle || '-' }}</td>
                <td>{{ item.taskType }}</td>
                <td>
                  <span class="badge" :class="item.resultStatus === 'SUCCESS' ? 'badge-success' : 'badge-warm'">
                    {{ item.resultStatus }}
                  </span>
                </td>
                <td class="text-sm text-slate-500">{{ item.message }}</td>
                <td>
                  <button class="btn btn-secondary" @click="viewLogDetail(item.id)">明细</button>
                </td>
              </tr>
              <tr v-if="!logs.length">
                <td colspan="6" class="table-empty">当前没有日志记录</td>
              </tr>
            </tbody>
          </table>
          <AppPagination
            :page-num="logPagination.pageNum"
            :page-size="logPagination.pageSize"
            :total="logPagination.total"
            :total-pages="logPagination.totalPages"
            @change-page="handleLogPageChange"
            @change-size="handleLogPageSizeChange"
          />

          <div v-if="logDetail" class="mt-6 rounded-[1.6rem] border border-slate-200 bg-white/70 p-5">
            <div class="flex flex-col gap-2">
              <div class="text-lg font-bold text-slate-900">日志明细 #{{ logDetail.id }}</div>
              <div class="text-sm text-slate-500">
                {{ logDetail.productTitle || '-' }} / {{ logDetail.taskType }} / {{ logDetail.resultStatus }}
              </div>
              <div class="text-sm text-slate-500">{{ logDetail.message }}</div>
            </div>
            <div v-if="structuredLog.environment" class="mt-4 rounded-[1.2rem] border border-slate-200 bg-white/85 p-4">
              <div class="field-label">发布环境</div>
              <div class="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div class="settings-card">
                  <div class="field-label">Endpoint</div>
                  <div class="text-sm text-slate-700 break-all">{{ structuredLog.environment.etsyEndpoint || '-' }}</div>
                </div>
                <div class="settings-card">
                  <div class="field-label">Shop</div>
                  <div class="text-sm text-slate-700">{{ structuredLog.environment.shopName || '-' }} / {{ structuredLog.environment.shopId || '-' }}</div>
                </div>
                <div class="settings-card">
                  <div class="field-label">Resolved IDs</div>
                  <div class="text-sm text-slate-700">
                    Section {{ structuredLog.environment.resolvedSectionId || '-' }} / Shipping {{ structuredLog.environment.resolvedShippingProfileId || '-' }}
                  </div>
                </div>
                <div class="settings-card">
                  <div class="field-label">Media</div>
                  <div class="text-sm text-slate-700">
                    图片 {{ structuredLog.environment.imageCount ?? 0 }} / 视频 {{ structuredLog.environment.videoCount ?? 0 }}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="structuredLog.steps.length" class="mt-4 rounded-[1.2rem] border border-slate-200 bg-white/85 p-4">
              <div class="field-label">发布步骤</div>
              <div class="mt-4 space-y-4">
                <div
                  v-for="step in structuredLog.steps"
                  :key="step.key"
                  class="rounded-[1rem] border border-slate-200 bg-slate-50/80 p-4"
                >
                  <div class="flex flex-wrap items-center justify-between gap-3">
                    <div class="text-base font-semibold text-slate-900">{{ step.title }}</div>
                    <span class="badge" :class="step.status === 'FAILED' ? 'badge-warm' : 'badge-success'">{{ step.status }}</span>
                  </div>
                  <div v-if="step.summary" class="mt-2 text-sm text-slate-500">{{ step.summary }}</div>
                  <div class="mt-3 grid gap-4 xl:grid-cols-2">
                    <div>
                      <label class="field-label">请求</label>
                      <pre class="code-panel">{{ formatStructuredValue(step.request) }}</pre>
                    </div>
                    <div>
                      <label class="field-label">响应</label>
                      <pre class="code-panel">{{ formatStructuredValue(step.response) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <label class="field-label">提示词快照</label>
              <pre class="code-panel">{{ logDetail.promptText || '-' }}</pre>
            </div>
            <div class="mt-4">
              <label class="field-label">第三方请求载荷</label>
              <pre class="code-panel">{{ logDetail.requestPayload || '-' }}</pre>
            </div>
            <div class="mt-4">
              <label class="field-label">第三方响应载荷</label>
              <pre class="code-panel">{{ logDetail.responsePayload || '-' }}</pre>
            </div>
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-secondary" type="button" @click="closeLogsModal">关闭日志</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import AppPagination from '@/components/AppPagination.vue';
import { etsyApi, fileApi, productApi, promptApi, variationTemplateApi } from '@/api';
import { confirmAction, notifyError, notifyInfo, notifySuccess } from '@/utils/feedback';
import {
  ETSY_WHEN_MADE_OPTIONS,
  ETSY_WHO_MADE_OPTIONS,
  normalizeEtsyWhenMade,
  normalizeEtsyWhoMade
} from '@/constants/etsy';

const defaultForm = () => ({
  id: null,
  title: '',
  descriptionText: '',
  category: '',
  sectionName: '',
  sectionId: null,
  tags: '',
  productType: '',
  sku: '',
  price: 0,
  quantity: 25,
  whoMade: 'i_did',
  whatIsIt: 'finished product',
  whenWasItMade: 'made_to_order',
  renewalOptions: 'automatic',
  materials: '',
  productionPartners: '',
  variation1: '',
  v1Option: '',
  variation2: '',
  v2Option: '',
  varPrice: null,
  taxonomyId: null,
  shippingProfileId: null,
  readinessStateId: null,
  variationTemplateId: null,
  variationTemplateItemId: null,
  etsyVariationSelection: {
    mode: 'NONE',
    property1: null,
    property2: null
  },
  video1: '',
  descriptionPrompt: '',
  aiResultJson: '',
  etsyPayloadJson: '',
  etsyListingId: null,
  etsyListingUrl: ''
});

const etsyWhoMadeOptions = ETSY_WHO_MADE_OPTIONS;
const etsyWhenMadeOptions = ETSY_WHEN_MADE_OPTIONS;

const emptyPreview = () => ({
  title: '',
  description: '',
  category: '',
  tags: '',
  suggestedPrice: null,
  keywords: [],
  altTexts: [],
  aiResultJson: '',
  applied: false
});

const form = reactive(defaultForm());
const previewResult = reactive(emptyPreview());
const products = ref([]);
const mediaAssets = ref([]);
const variationTemplates = ref([]);
const etsySections = ref([]);
const etsyShippingProfiles = ref([]);
const etsyVariationMetadata = ref(null);
const infoForm = reactive(defaultForm());
const infoMediaIds = ref([]);
const selectedIds = ref([]);
const selectedMediaIds = ref([]);
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0
});
const descriptionPrompts = ref([]);
const descriptionTemplateId = ref('');
const templateVariableValues = reactive({});
const keyword = ref('');
const editorVisible = ref(false);
const infoVisible = ref(false);
const promptSaveVisible = ref(false);
const logsVisible = ref(false);
const logs = ref([]);
const logDetail = ref(null);
const logTargetTitle = ref('');
const currentLogProductId = ref(null);
const logPagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0
});
const promptSaveForm = reactive({
  name: '',
  content: ''
});
const structuredLog = computed(() => buildStructuredLog(logDetail.value));

const selectedMedia = computed(() => mediaAssets.value.filter((item) => selectedMediaIds.value.includes(item.id)));
const selectedDescriptionTemplate = computed(() => descriptionPrompts.value
  .find((item) => String(item.id) === String(descriptionTemplateId.value)));
const selectedTemplateVariables = computed(() => extractTemplateVariables(selectedDescriptionTemplate.value));
const selectedVariationTemplate = computed(() => findVariationTemplate(form.variationTemplateId));
const selectedVariationItems = computed(() => selectedVariationTemplate.value?.items || []);
const selectedVariationItem = computed(() => findVariationItem(form.variationTemplateId, form.variationTemplateItemId));
const selectedInfoVariationTemplate = computed(() => findVariationTemplate(infoForm.variationTemplateId));
const selectedInfoVariationItems = computed(() => selectedInfoVariationTemplate.value?.items || []);

function normalizeVariationSelection(selection) {
  return {
    mode: selection?.mode || 'NONE',
    property1: selection?.property1
      ? {
        propertyId: selection.property1.propertyId ?? null,
        name: selection.property1.name || '',
        scaleId: selection.property1.scaleId ?? null,
        scaleName: selection.property1.scaleName || '',
        values: Array.isArray(selection.property1.values)
          ? selection.property1.values.map((item) => ({
            valueId: item.valueId ?? null,
            value: item.value || '',
            scaleId: item.scaleId ?? null
          }))
          : []
      }
      : null,
    property2: selection?.property2
      ? {
        propertyId: selection.property2.propertyId ?? null,
        name: selection.property2.name || '',
        scaleId: selection.property2.scaleId ?? null,
        scaleName: selection.property2.scaleName || '',
        values: Array.isArray(selection.property2.values)
          ? selection.property2.values.map((item) => ({
            valueId: item.valueId ?? null,
            value: item.value || '',
            scaleId: item.scaleId ?? null
          }))
          : []
      }
      : null
  };
}

function resetForm() {
  Object.assign(form, defaultForm());
  Object.assign(previewResult, emptyPreview());
  mediaAssets.value = [];
  selectedMediaIds.value = [];
  descriptionTemplateId.value = '';
  clearTemplateVariables();
}

function patchInfoForm(data) {
  const next = defaultForm();
  Object.keys(next).forEach((key) => {
    next[key] = data[key] ?? next[key];
  });
  next.whoMade = normalizeEtsyWhoMade(next.whoMade);
  next.whenWasItMade = normalizeEtsyWhenMade(next.whenWasItMade);
  next.etsyVariationSelection = normalizeVariationSelection(data.etsyVariationSelection);
  Object.assign(infoForm, next);
}

function patchForm(data) {
  const next = defaultForm();
  Object.keys(next).forEach((key) => {
    next[key] = data[key] ?? next[key];
  });
  next.whoMade = normalizeEtsyWhoMade(next.whoMade);
  next.whenWasItMade = normalizeEtsyWhenMade(next.whenWasItMade);
  next.etsyVariationSelection = normalizeVariationSelection(data.etsyVariationSelection);
  Object.assign(form, next);
}

function normalizeMedia(item) {
  return {
    id: item.id,
    imageType: item.imageType || item.mediaType || 'IMAGE',
    publicUrl: item.publicUrl || item.url,
    originalName: item.originalName || item.name || '媒体文件',
    sortOrder: item.sortOrder || 0
  };
}

function extractTemplateVariables(template) {
  if (!template) {
    return [];
  }
  const serverVariables = (template.variables || [])
    .map((item) => ({ name: item?.name || '' }))
    .filter((item) => item.name);
  if (serverVariables.length) {
    return serverVariables;
  }
  const matches = String(template.content || '').match(/\{\{\s*([\w\u4e00-\u9fa5-]+)\s*\}\}/g) || [];
  return [...new Set(matches.map((item) => item.replace(/\{\{|\}\}/g, '').trim()))]
    .filter(Boolean)
    .map((name) => ({ name }));
}

function clearTemplateVariables() {
  Object.keys(templateVariableValues).forEach((key) => {
    delete templateVariableValues[key];
  });
}

function syncTemplateVariables() {
  const next = {};
  selectedTemplateVariables.value.forEach((item) => {
    next[item.name] = templateVariableValues[item.name] || '';
  });
  clearTemplateVariables();
  Object.keys(next).forEach((key) => {
    templateVariableValues[key] = next[key];
  });
}

function findVariationTemplate(templateId) {
  return variationTemplates.value.find((item) => String(item.id) === String(templateId));
}

function findVariationItem(templateId, itemId) {
  const template = findVariationTemplate(templateId);
  if (!template) {
    return null;
  }
  return (template.items || []).find((item) => String(item.id) === String(itemId)) || null;
}

function findEtsySection(sectionId) {
  return etsySections.value.find((item) => String(item.sectionId) === String(sectionId)) || null;
}

function findShippingProfile(shippingProfileId) {
  return etsyShippingProfiles.value.find((item) => String(item.shippingProfileId) === String(shippingProfileId)) || null;
}

function selectedShippingProfileLabel(shippingProfileId) {
  const profile = findShippingProfile(shippingProfileId);
  if (profile) {
    return `${profile.title || `Profile #${profile.shippingProfileId}`} / ${profile.shippingProfileId}`;
  }
  if (shippingProfileId) {
    return `Profile #${shippingProfileId}`;
  }
  return '自动解析';
}

function formatVariationItemLabel(template, item) {
  if (!item) {
    return '';
  }
  const parts = [];
  if (template?.variation1Name || item.option1Value) {
    parts.push(`${template?.variation1Name || 'Variation 1'}: ${item.option1Value || '-'}`);
  }
  if (template?.variation2Name || item.option2Value) {
    parts.push(`${template?.variation2Name || 'Variation 2'}: ${item.option2Value || '-'}`);
  }
  parts.push(`Var Price: ${item.varPrice ?? '-'}`);
  return parts.join(' / ');
}

function clearVariationFields(target) {
  target.sectionName = '';
  target.sectionId = null;
  target.variation1 = '';
  target.v1Option = '';
  target.variation2 = '';
  target.v2Option = '';
  target.varPrice = null;
  target.variationTemplateId = null;
  target.variationTemplateItemId = null;
}

function applyVariationTemplate(target, templateId, { selectFirstItem = true } = {}) {
  const template = findVariationTemplate(templateId);
  if (!template) {
    clearVariationFields(target);
    return;
  }
  target.etsyVariationSelection = normalizeVariationSelection({
    ...(target.etsyVariationSelection || {}),
    mode: 'TEMPLATE'
  });
  target.variationTemplateId = template.id;
  if (!target.sectionId) {
    target.sectionName = template.sectionName || '';
  }
  target.price = template.basePrice ?? target.price ?? 0;
  target.quantity = template.defaultQuantity ?? 25;
  target.variation1 = template.variation1Name || '';
  target.variation2 = template.variation2Name || '';
  if (selectFirstItem) {
    const firstItem = (template.items || [])[0] || null;
    target.variationTemplateItemId = firstItem ? firstItem.id : null;
    applyVariationItem(target, template.id, target.variationTemplateItemId);
  }
}

function applyVariationItem(target, templateId, itemId) {
  const template = findVariationTemplate(templateId);
  const item = findVariationItem(templateId, itemId);
  if (!template) {
    clearVariationFields(target);
    return;
  }
  target.variationTemplateId = template.id;
  target.variation1 = template.variation1Name || '';
  target.variation2 = template.variation2Name || '';
  if (!item) {
    target.variationTemplateItemId = null;
    target.v1Option = '';
    target.v2Option = '';
    target.varPrice = null;
    return;
  }
  target.variationTemplateItemId = item.id;
  target.v1Option = item.option1Value || '';
  target.v2Option = item.option2Value || '';
  target.varPrice = item.varPrice ?? null;
}

function syncSectionFields(target, sectionId) {
  const section = findEtsySection(sectionId);
  const variationTemplate = findVariationTemplate(target.variationTemplateId);
  target.sectionId = section ? section.sectionId : null;
  target.sectionName = section ? section.title : (variationTemplate?.sectionName || '');
}

function officialPropertyOptions() {
  return etsyVariationMetadata.value?.properties || [];
}

function findOfficialProperty(propertyId) {
  return officialPropertyOptions().find((item) => String(item.propertyId) === String(propertyId)) || null;
}

function applyOfficialProperty(target, key, propertyId) {
  if (!target.etsyVariationSelection) {
    target.etsyVariationSelection = normalizeVariationSelection();
  }
  const property = findOfficialProperty(propertyId);
  if (!property) {
    target.etsyVariationSelection[key] = null;
    return;
  }
  target.etsyVariationSelection[key] = {
    propertyId: property.propertyId,
    name: property.name,
    scaleId: property.scaleId ?? null,
    scaleName: property.scaleName || '',
    values: []
  };
}

function toggleOfficialValue(target, key, propertyValue, checked) {
  const property = target.etsyVariationSelection?.[key];
  if (!property) {
    return;
  }
  const currentValues = Array.isArray(property.values) ? property.values : [];
  if (checked) {
    if (!currentValues.some((item) => String(item.valueId) === String(propertyValue.valueId) && item.value === propertyValue.value)) {
      currentValues.push({
        valueId: propertyValue.valueId ?? null,
        value: propertyValue.value || '',
        scaleId: propertyValue.scaleId ?? null
      });
    }
  } else {
    property.values = currentValues.filter((item) =>
      !(String(item.valueId) === String(propertyValue.valueId) && item.value === propertyValue.value));
    return;
  }
  property.values = currentValues;
}

function isOfficialValueChecked(target, key, propertyValue) {
  const values = target.etsyVariationSelection?.[key]?.values || [];
  return values.some((item) => String(item.valueId) === String(propertyValue.valueId) && item.value === propertyValue.value);
}

function selectedOfficialValues(key) {
  return infoForm.etsyVariationSelection?.[key]?.values || [];
}

function variationModeLabel(mode) {
  if (mode === 'TEMPLATE') {
    return '变量模板';
  }
  if (mode === 'OFFICIAL') {
    return 'Etsy 官方属性';
  }
  return '不启用';
}

function parsePayloadObject(payload) {
  if (!payload) {
    return null;
  }
  if (typeof payload === 'object') {
    return payload;
  }
  try {
    const parsed = JSON.parse(payload);
    if (parsed && typeof parsed.previous === 'string') {
      try {
        parsed.previous = JSON.parse(parsed.previous);
      } catch (error) {
        parsed.previous = parsed.previous || null;
      }
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function formatStructuredValue(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

function buildStructuredLog(detail) {
  const empty = { environment: null, steps: [] };
  if (!detail) {
    return empty;
  }
  const request = parsePayloadObject(detail.requestPayload) || {};
  const response = parsePayloadObject(detail.responsePayload) || {};
  const responseBody = response.previous && typeof response.previous === 'object' ? response.previous : response;
  const failedStep = responseBody.failedStep || response.failedStep || '';
  const stepDefs = [
    { key: 'createDraft', title: '1. 创建草稿' },
    { key: 'updateSection', title: '2. 绑定 Section' },
    { key: 'updateInventory', title: '3. 同步 Variations / Inventory' },
    { key: 'uploadImages', title: '4. 上传图片' },
    { key: 'uploadVideo', title: '5. 上传视频' }
  ];
  const steps = stepDefs
    .filter((item) => request[item.key] !== undefined || responseBody[item.key] !== undefined || failedStep.indexOf(item.key) >= 0)
    .map((item) => ({
      key: item.key,
      title: item.title,
      status: failedStep.indexOf(item.key) >= 0 ? 'FAILED' : 'SUCCESS',
      summary: stepSummary(item.key, request[item.key], responseBody[item.key], failedStep, response.error),
      request: request[item.key] ?? null,
      response: responseBody[item.key] ?? (failedStep.indexOf(item.key) >= 0 ? response.error : null)
    }));
  return {
    environment: request.environment || null,
    steps
  };
}

function stepSummary(key, requestValue, responseValue, failedStep, error) {
  if (failedStep && failedStep.indexOf(key) >= 0) {
    return error || '该步骤执行失败';
  }
  if (key === 'uploadImages' && Array.isArray(requestValue)) {
    return `共提交 ${requestValue.length} 张图片`;
  }
  if (key === 'uploadVideo' && requestValue) {
    return '已提交 1 个视频文件';
  }
  if (key === 'updateInventory' && requestValue?.products) {
    return `共同步 ${requestValue.products.length} 个变体组合`;
  }
  if (key === 'updateSection' && requestValue?.section_id) {
    return `目标 Section ID：${requestValue.section_id}`;
  }
  if (key === 'createDraft' && responseValue?.listing_id) {
    return `Listing ID：${responseValue.listing_id}`;
  }
  return '';
}

function officialVariationCount() {
  if (infoForm.etsyVariationSelection?.mode !== 'OFFICIAL') {
    return 0;
  }
  const count1 = selectedOfficialValues('property1').length || 1;
  const count2 = selectedOfficialValues('property2').length || 1;
  if (!infoForm.etsyVariationSelection?.property1 && !infoForm.etsyVariationSelection?.property2) {
    return 0;
  }
  return count1 * count2;
}

function badgeClass(status) {
  if (status === 'ETSY_DRAFT') {
    return 'badge-success';
  }
  if (status === 'AI_READY') {
    return 'badge-warm';
  }
  if (status === 'AI_PREVIEW') {
    return 'badge-muted';
  }
  return 'badge-muted';
}

function previewImages(item) {
  return (item.mediaAssets || []).filter((asset) => asset.imageType !== 'VIDEO').slice(0, 3);
}

function countImages(list = []) {
  return (list || []).filter((item) => item.imageType !== 'VIDEO').length;
}

function countVideos(list = []) {
  return (list || []).filter((item) => item.imageType === 'VIDEO').length;
}

function orderedSelectedMediaIds() {
  return mediaAssets.value
    .filter((item) => selectedMediaIds.value.includes(item.id))
    .map((item) => item.id);
}

function syncVideoField() {
  const selectedVideo = mediaAssets.value.find(
    (item) => selectedMediaIds.value.includes(item.id) && item.imageType === 'VIDEO'
  );
  if (selectedVideo) {
    form.video1 = selectedVideo.publicUrl;
    return;
  }
  if (form.video1 && mediaAssets.value.some((item) => item.imageType === 'VIDEO')) {
    form.video1 = '';
  }
}

function validateMediaSelection(existingList, files = []) {
  const currentImages = countImages(existingList);
  const currentVideos = countVideos(existingList);
  const incomingImages = files.filter((file) => !file.type.startsWith('video/')).length;
  const incomingVideos = files.filter((file) => file.type.startsWith('video/')).length;
  if (currentImages + incomingImages > 20) {
    notifyError('单个产品最多支持 20 张图片');
    return false;
  }
  if (currentVideos + incomingVideos > 1) {
    notifyError('单个产品最多支持 1 个视频');
    return false;
  }
  return true;
}

function openCreateModal() {
  resetForm();
  editorVisible.value = true;
}

async function openEditModal(id) {
  await loadDetail(id);
  editorVisible.value = true;
}

function closeEditorModal() {
  editorVisible.value = false;
}

async function openInfoModal(id) {
  try {
    const res = await productApi.detail(id);
    patchInfoForm(res.data);
    infoMediaIds.value = (res.data.mediaAssets || res.data.images || []).map((item) => item.id);
    etsyVariationMetadata.value = null;
    infoVisible.value = true;
  } catch (error) {
    notifyError(error.message || '加载产品信息失败');
  }
}

function closeInfoModal() {
  infoVisible.value = false;
  Object.assign(infoForm, defaultForm());
  infoMediaIds.value = [];
  etsyVariationMetadata.value = null;
}

function openPromptSaveModal() {
  if (!form.descriptionPrompt) {
    notifyError('请先填写提示词');
    return;
  }
  promptSaveForm.name = '';
  promptSaveForm.content = form.descriptionPrompt;
  promptSaveVisible.value = true;
}

function closePromptSaveModal() {
  promptSaveVisible.value = false;
  promptSaveForm.name = '';
  promptSaveForm.content = '';
}

async function openLogsModal(product) {
  logsVisible.value = true;
  logTargetTitle.value = product ? product.title : '';
  currentLogProductId.value = product ? product.id : null;
  logDetail.value = null;
  logPagination.pageNum = 1;
  await loadLogs();
}

function closeLogsModal() {
  logsVisible.value = false;
  logs.value = [];
  logDetail.value = null;
  logTargetTitle.value = '';
  currentLogProductId.value = null;
}

async function viewLogDetail(logId) {
  try {
    const res = await productApi.logDetail(logId);
    logDetail.value = res.data;
  } catch (error) {
    notifyError(error.message || '加载日志明细失败');
  }
}

async function loadPrompts() {
  const descRes = await promptApi.list({ type: 'DESCRIPTION', pageNum: 1, pageSize: 200 });
  descriptionPrompts.value = descRes.data.records || [];
}

async function loadEtsySections() {
  try {
    const res = await etsyApi.listSections();
    etsySections.value = res.data || [];
  } catch (error) {
    etsySections.value = [];
  }
}

async function loadEtsyShippingProfiles() {
  try {
    const res = await etsyApi.listShippingProfiles();
    etsyShippingProfiles.value = res.data || [];
  } catch (error) {
    etsyShippingProfiles.value = [];
  }
}

async function loadInfoVariationMetadata() {
  if (!infoForm.id) {
    notifyError('请先保存产品信息');
    return;
  }
  try {
    const res = await productApi.getEtsyVariationMetadata(infoForm.id);
    etsyVariationMetadata.value = res.data || null;
    infoForm.taxonomyId = res.data?.taxonomyId || infoForm.taxonomyId;
    notifyInfo('Etsy 官方变体属性已加载，请选择属性和值');
  } catch (error) {
    notifyError(error.message || '加载 Etsy 官方变体属性失败');
  }
}

async function loadVariationTemplates() {
  const res = await variationTemplateApi.list({ pageNum: 1, pageSize: 200 });
  variationTemplates.value = res.data.records || [];
}

async function loadProducts() {
  const res = await productApi.list({
    keyword: keyword.value || undefined,
    pageNum: pagination.pageNum,
    pageSize: pagination.pageSize
  });
  products.value = (res.data.records || []).map((item) => ({
    ...item,
    mediaAssets: (item.mediaAssets || item.images || []).map(normalizeMedia)
  }));
  pagination.total = res.data.total || 0;
  pagination.totalPages = res.data.totalPages || 0;
  selectedIds.value = [];
  if (!products.value.length && pagination.pageNum > 1 && pagination.total > 0) {
    pagination.pageNum -= 1;
    await loadProducts();
  }
}

async function loadLogs() {
  try {
    const res = await productApi.logs({
      productId: currentLogProductId.value || undefined,
      pageNum: logPagination.pageNum,
      pageSize: logPagination.pageSize
    });
    logs.value = res.data.records || [];
    logPagination.total = res.data.total || 0;
    logPagination.totalPages = res.data.totalPages || 0;
    if (!logs.value.length && logPagination.pageNum > 1 && logPagination.total > 0) {
      logPagination.pageNum -= 1;
      await loadLogs();
    }
  } catch (error) {
    notifyError(error.message || '加载日志失败');
  }
}

async function loadAll() {
  try {
    await Promise.all([loadProducts(), loadPrompts(), loadVariationTemplates(), loadEtsySections(), loadEtsyShippingProfiles()]);
  } catch (error) {
    notifyError(error.message || '加载数据失败');
  }
}

async function saveProduct() {
  const payload = {
    ...form,
    mediaIds: orderedSelectedMediaIds()
  };
  try {
    if (form.id) {
      await productApi.update(form.id, payload);
    } else {
      const res = await productApi.create(payload);
      form.id = res.data.id;
    }
    await loadProducts();
    notifySuccess('媒体与提示词已保存');
  } catch (error) {
    notifyError(error.message || '保存失败');
  }
}

async function loadDetail(id) {
  try {
    const res = await productApi.detail(id);
    patchForm(res.data);
    Object.assign(previewResult, emptyPreview());
    mediaAssets.value = (res.data.mediaAssets || res.data.images || []).map(normalizeMedia);
    selectedMediaIds.value = mediaAssets.value.map((item) => item.id);
  } catch (error) {
    notifyError(error.message || '加载产品详情失败');
  }
}

function handleEditorVariationTemplateChange() {
  if (form.variationTemplateId) {
    form.etsyVariationSelection = normalizeVariationSelection({
      ...(form.etsyVariationSelection || {}),
      mode: 'TEMPLATE'
    });
  }
  applyVariationTemplate(form, form.variationTemplateId);
}

function handleEditorVariationItemChange() {
  applyVariationItem(form, form.variationTemplateId, form.variationTemplateItemId);
}

function handleEditorSectionChange() {
  syncSectionFields(form, form.sectionId);
}

function handleInfoVariationTemplateChange() {
  if (infoForm.variationTemplateId) {
    infoForm.etsyVariationSelection = normalizeVariationSelection({
      ...(infoForm.etsyVariationSelection || {}),
      mode: 'TEMPLATE'
    });
  }
  applyVariationTemplate(infoForm, infoForm.variationTemplateId);
}

function handleInfoVariationItemChange() {
  applyVariationItem(infoForm, infoForm.variationTemplateId, infoForm.variationTemplateItemId);
}

function handleInfoSectionChange() {
  syncSectionFields(infoForm, infoForm.sectionId);
}

function handleInfoVariationModeChange() {
  infoForm.etsyVariationSelection = normalizeVariationSelection(infoForm.etsyVariationSelection);
  if (infoForm.etsyVariationSelection.mode !== 'OFFICIAL') {
    etsyVariationMetadata.value = null;
    if (infoForm.etsyVariationSelection.mode !== 'TEMPLATE') {
      infoForm.variationTemplateId = null;
      infoForm.variationTemplateItemId = null;
    }
    infoForm.etsyVariationSelection.property1 = null;
    infoForm.etsyVariationSelection.property2 = null;
  }
  if (infoForm.etsyVariationSelection.mode === 'TEMPLATE' && infoForm.variationTemplateId) {
    applyVariationTemplate(infoForm, infoForm.variationTemplateId, { selectFirstItem: false });
  }
}

function handleOfficialPropertyChange(key, propertyId) {
  const normalized = propertyId === '' || propertyId === 'null' ? null : propertyId;
  applyOfficialProperty(infoForm, key, normalized);
}

async function removeProduct(id) {
  if (!(await confirmAction({
    title: '删除产品记录',
    message: '确认删除这条产品记录吗？相关媒体绑定和日志仍会保留历史引用。',
    confirmText: '确认删除',
    tone: 'danger'
  }))) {
    return;
  }
  try {
    await productApi.remove(id);
    if (products.value.length === 1 && pagination.pageNum > 1) {
      pagination.pageNum -= 1;
    }
    await loadProducts();
    notifySuccess('产品记录已删除');
  } catch (error) {
    notifyError(error.message || '删除失败');
  }
}

async function handleFiles(event) {
  const files = Array.from(event.target.files || []);
  if (!files.length) {
    return;
  }
  if (!validateMediaSelection(selectedMedia.value, files)) {
    event.target.value = '';
    return;
  }
  try {
    const res = await fileApi.uploadBatch(files);
    const uploaded = (res.data || []).map(normalizeMedia);
    mediaAssets.value = mediaAssets.value.concat(uploaded);
    selectedMediaIds.value = mediaAssets.value.map((item) => item.id);
    syncVideoField();
    notifySuccess('媒体批量上传成功');
  } catch (error) {
    notifyError(error.message || '批量上传失败');
  } finally {
    event.target.value = '';
  }
}

async function generateDescription() {
  if (!form.id) {
    notifyError('请先保存产品');
    return;
  }
  const missingVariable = selectedTemplateVariables.value.find((item) => !String(templateVariableValues[item.name] || '').trim());
  if (missingVariable) {
    notifyError(`请先填写模板变量：${missingVariable.name}`);
    return;
  }
  try {
    const res = await productApi.generateDescription(form.id, {
      templateId: descriptionTemplateId.value || null,
      customPrompt: form.descriptionPrompt,
      templateVariables: { ...templateVariableValues }
    });
    Object.assign(previewResult, emptyPreview(), res.data || {});
    notifyInfo('JSON 预览已生成，请确认是否应用');
    await loadProducts();
  } catch (error) {
    notifyError(error.message || '生成 JSON 失败');
  }
}

async function applyAiResult() {
  if (!form.id) {
    return;
  }
  if (!previewResult.aiResultJson && !form.aiResultJson) {
    notifyError('当前没有可应用的 JSON');
    return;
  }
  if (!(await confirmAction({
    title: '应用 AI JSON',
    message: '确认将这份 AI JSON 应用到产品信息吗？现有标题、描述、标签和价格等字段会被整体更新。',
    confirmText: '确认应用'
  }))) {
    return;
  }
  try {
    const res = await productApi.applyAiResult(form.id, {
      aiResultJson: previewResult.aiResultJson || form.aiResultJson
    });
    patchForm(res.data);
    Object.assign(previewResult, emptyPreview());
    mediaAssets.value = (res.data.mediaAssets || res.data.images || []).map(normalizeMedia);
    selectedMediaIds.value = mediaAssets.value.map((item) => item.id);
    await loadProducts();
    notifySuccess('AI 结果已应用');
  } catch (error) {
    notifyError(error.message || '应用 AI 结果失败');
  }
}

async function publishToEtsy(id) {
  try {
    const target = products.value.find((item) => item.id === id) || (form.id === id ? form : null);
    if (target?.variationTemplateId) {
      notifyInfo('正在发布到 Etsy，将按变量模板自动同步变体、价格和库存');
    } else {
      notifyInfo('正在发布到 Etsy，同步草稿、图片和变体，请稍候');
    }
    const res = await productApi.publishToEtsyDraft(id);
    if (form.id === id) {
      form.etsyListingId = res.data.listingId || null;
      form.etsyListingUrl = res.data.listingUrl || '';
      form.etsyPayloadJson = res.data.etsyPayloadJson || '';
    }
    if (infoVisible.value && infoForm.id === id) {
      infoForm.etsyListingId = res.data.listingId || null;
      infoForm.etsyListingUrl = res.data.listingUrl || '';
      infoForm.etsyPayloadJson = res.data.etsyPayloadJson || '';
    }
    await loadProducts();
    notifySuccess(res.data.message || '已发送到 Etsy');
  } catch (error) {
    notifyError(error.message || '发送 Etsy 失败');
  }
}

async function savePromptToLibrary() {
  if (!promptSaveForm.name || !promptSaveForm.content) {
    notifyError('请填写提示词名称和内容');
    return;
  }
  try {
    await promptApi.create({
      name: promptSaveForm.name,
      type: 'DESCRIPTION',
      content: promptSaveForm.content
    });
    await loadPrompts();
    closePromptSaveModal();
    notifySuccess('提示词已保存到库中');
  } catch (error) {
    notifyError(error.message || '保存提示词失败');
  }
}

async function saveInfoForm() {
  if (!infoForm.id) {
    return;
  }
  if (String(infoForm.variation1 || '').trim().length > 45 || String(infoForm.variation2 || '').trim().length > 45) {
    notifyError('Variation 名称不能超过 45 个字符');
    return;
  }
  if (String(infoForm.v1Option || '').trim().length > 20 || String(infoForm.v2Option || '').trim().length > 20) {
    notifyError('Variation 选项值不能超过 20 个字符');
    return;
  }
  if (infoForm.etsyVariationSelection?.mode === 'TEMPLATE' && !infoForm.variationTemplateId) {
    notifyError('当前选择了模板变体同步，请先选择变量模板');
    return;
  }
  if (infoForm.etsyVariationSelection?.mode === 'OFFICIAL') {
    const property1Values = infoForm.etsyVariationSelection?.property1?.values || [];
    const property2Values = infoForm.etsyVariationSelection?.property2?.values || [];
    if (
      infoForm.etsyVariationSelection?.property1?.propertyId
      && infoForm.etsyVariationSelection?.property2?.propertyId
      && String(infoForm.etsyVariationSelection.property1.propertyId) === String(infoForm.etsyVariationSelection.property2.propertyId)
    ) {
      notifyError('官方属性 1 和属性 2 不能选择同一个 Etsy 属性');
      return;
    }
    if (!property1Values.length && !property2Values.length) {
      notifyError('官方属性模式下，至少需要选择一组 Etsy 属性值');
      return;
    }
  }
  try {
    await productApi.update(infoForm.id, {
      ...infoForm,
      mediaIds: infoMediaIds.value
    });
    await loadProducts();
    if (form.id === infoForm.id) {
      Object.assign(form, infoForm);
    }
    notifySuccess('产品信息已保存');
  } catch (error) {
    notifyError(error.message || '保存产品信息失败');
  }
}

async function copyText(value) {
  try {
    await window.navigator.clipboard.writeText(value || '');
    notifySuccess('已复制');
  } catch (error) {
    notifyError('复制失败');
  }
}

function toggleAllSelection(event) {
  if (event.target.checked) {
    selectedIds.value = products.value.map((item) => item.id);
    return;
  }
  selectedIds.value = [];
}

function handlePageChange(pageNum) {
  pagination.pageNum = pageNum;
  loadProducts();
}

function handlePageSizeChange(pageSize) {
  pagination.pageSize = pageSize;
  pagination.pageNum = 1;
  loadProducts();
}

function handleLogPageChange(pageNum) {
  logPagination.pageNum = pageNum;
  loadLogs();
}

function handleLogPageSizeChange(pageSize) {
  logPagination.pageSize = pageSize;
  logPagination.pageNum = 1;
  loadLogs();
}

async function exportCsv() {
  try {
    const response = await productApi.exportCsv(selectedIds.value);
    const blob = response instanceof Blob ? response : response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'epintong-etsy-export.csv';
    link.click();
    window.URL.revokeObjectURL(url);
    notifySuccess('CSV 已导出');
  } catch (error) {
    notifyError(error.message || '导出失败');
  }
}

watch(keyword, () => {
  pagination.pageNum = 1;
  loadProducts();
});

watch(selectedMediaIds, () => {
  syncVideoField();
}, { deep: true });

watch(descriptionTemplateId, () => {
  syncTemplateVariables();
});

onMounted(loadAll);
</script>
